/**
 * The flashcards start page (brief §flashcards: `/flashcards`). At the top "Dagens repetition ·
 * alla moment": one mixed queue across the moments, started with `?start=mixed` (the review page
 * fetches it). Below, the stats, and the per-moment decks (due/new/learned counts, an optional
 * topic filter, "Repetera förfallna" / "Lär nya") folded into "Bläddra per moment". Starting a
 * deck fetches its queue once (`GET /api/flashcards/queue`) and hands it to the review page
 * through `session.ts`, the same pattern `practice/Page.tsx` uses.
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import { useLocalizedText } from "@client/components/Localized";
import {
  Badge,
  Button,
  Card,
  Modal,
  PageHeader,
  Select,
  Spinner,
  StatTile,
  useToast,
} from "@client/components/ui";
import { useLang, useT, useTp } from "@client/i18n";
import { messageForError } from "@client/lib/errors";
import { formatDayShort, formatPercent } from "@client/lib/format";
import { DEFAULT_TIMEZONE, dayKey } from "@shared/time";
import { useDecks, useFlashcardStats, useStartQueue, useTopics } from "./api";
import { createReviewSession, isReviewComplete, loadSession, saveSession } from "./session";
import type { DeckSummary, MixedSummary } from "./types";

function DeckCard({
  deck,
  topic,
  topicOptions,
  onTopicChange,
  onStart,
  starting,
}: {
  deck: DeckSummary;
  topic: string;
  topicOptions: { value: string; label: string }[];
  onTopicChange: (topic: string) => void;
  onStart: (kind: "due" | "new") => void;
  starting: boolean;
}) {
  const t = useT();
  const tp = useTp();
  const localize = useLocalizedText();

  return (
    <Card title={`${deck.chapter}. ${localize(deck.title)}`} compact>
      <div className="flex flex-wrap gap-2">
        <Badge tone="neutral">{tp("flashcards.deck.total", deck.total)}</Badge>
        <Badge tone={deck.due > 0 ? "warning" : "neutral"}>
          {tp("flashcards.deck.due", deck.due)}
        </Badge>
        <Badge tone={deck.newCards > 0 ? "accent" : "neutral"}>
          {tp("flashcards.deck.new", deck.newCards)}
        </Badge>
        <Badge tone="success">{tp("flashcards.deck.learned", deck.learned)}</Badge>
      </div>

      {topicOptions.length > 1 && (
        <div className="mt-3 max-w-xs">
          <Select
            label={t("flashcards.deck.topicFilter.label")}
            value={topic}
            onChange={(event) => onTopicChange(event.target.value)}
            options={topicOptions}
          />
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          size="sm"
          className="max-sm:min-h-11"
          onClick={() => onStart("due")}
          disabled={deck.due === 0}
          loading={starting}
        >
          {t("flashcards.deck.reviewDue")}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          className="max-sm:min-h-11"
          onClick={() => onStart("new")}
          disabled={deck.newCards === 0}
          loading={starting}
        >
          {t("flashcards.deck.learnNew")}
        </Button>
      </div>
    </Card>
  );
}

/** "Dagens repetition · alla moment": the mixed queue across every moment. */
function MixedCard({ mixed, onStart }: { mixed: MixedSummary | undefined; onStart: () => void }) {
  const t = useT();
  const tp = useTp();
  // The counts are unknown until the decks arrive; an empty queue is handled by the review page.
  const nothing = mixed !== undefined && mixed.due + mixed.newToday === 0;
  return (
    <Card title={t("flashcards.mixed.title")} description={t("flashcards.mixed.caption")}>
      {mixed && (
        <div className="mb-4 flex flex-wrap gap-2">
          <Badge tone={mixed.due > 0 ? "warning" : "neutral"}>
            {tp("flashcards.deck.due", mixed.due)}
          </Badge>
          <Badge tone={mixed.newToday > 0 ? "accent" : "neutral"}>
            {tp("flashcards.deck.new", mixed.newToday)}
          </Badge>
        </div>
      )}
      {nothing ? (
        <p className="text-sm text-ink-600 dark:text-ink-400">
          {t("flashcards.review.empty.title")}
        </p>
      ) : (
        <Button className="max-sm:min-h-11" onClick={onStart}>
          {t("flashcards.mixed.start")}
        </Button>
      )}
    </Card>
  );
}

function StatsPanel() {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const statsQuery = useFlashcardStats();
  const stats = statsQuery.data;

  if (statsQuery.isLoading) {
    return (
      <Card title={t("flashcards.stats.title")} compact>
        <Spinner size="sm" />
      </Card>
    );
  }
  if (!stats) return null;

  const totalReviewed = stats.reviewsPerDay.reduce((sum, day) => sum + day.count, 0);
  const maxDay = Math.max(1, ...stats.reviewsPerDay.map((day) => day.count));

  return (
    <Card title={t("flashcards.stats.title")} compact>
      {totalReviewed === 0 && stats.streak === 0 ? (
        <p className="text-sm text-ink-600 dark:text-ink-400">{t("flashcards.stats.noData")}</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <StatTile
            label={t("flashcards.stats.streak")}
            value={tp("flashcards.stats.streak.days", stats.streak)}
          />
          <StatTile
            label={t("flashcards.stats.retention")}
            value={stats.retention === null ? "—" : formatPercent(stats.retention, lang)}
          />
          <StatTile
            label={t("flashcards.stats.maturity.title")}
            value={stats.maturity.mature}
            hint={t("flashcards.stats.maturity.mature")}
          />
        </div>
      )}

      <p className="mb-2 mt-4 text-sm font-medium text-ink-700 dark:text-ink-200">
        {t("flashcards.stats.reviewsPerDay")}
      </p>
      <div
        className="flex h-16 items-end gap-0.5"
        role="img"
        aria-label={t("flashcards.stats.reviewsPerDay")}
      >
        {stats.reviewsPerDay.map((day) => (
          <div
            key={day.date}
            title={`${day.date}: ${day.count}`}
            className="min-w-[3px] flex-1 rounded-t bg-accent-400 dark:bg-accent-600"
            style={{ height: `${Math.max(2, (day.count / maxDay) * 100)}%` }}
          />
        ))}
      </div>
    </Card>
  );
}

export function FlashcardsPage() {
  const t = useT();
  const { lang } = useLang();
  const localize = useLocalizedText();
  const toast = useToast();
  const navigate = useNavigate();

  const decksQuery = useDecks();
  const topicsQuery = useTopics();
  const startQueue = useStartQueue();

  const [topicByChapter, setTopicByChapter] = useState<Record<number, string>>({});
  const [startingChapter, setStartingChapter] = useState<number | null>(null);
  const [savedSession] = useState(() => loadSession());
  const unfinished = savedSession !== null && !isReviewComplete(savedSession);
  // What the replace dialog will start once confirmed: the mixed review or one deck.
  const [replacement, setReplacement] = useState<
    { mixed: true } | { mixed: false; chapter: number; kind: "due" | "new" } | null
  >(null);

  function requestStart(chapter: number, kind: "due" | "new") {
    if (unfinished) setReplacement({ mixed: false, chapter, kind });
    else void start(chapter, kind);
  }

  function requestMixed() {
    if (unfinished) setReplacement({ mixed: true });
    else navigate("/flashcards/review?start=mixed");
  }

  async function start(chapter: number, kind: "due" | "new") {
    const topic = topicByChapter[chapter];
    setStartingChapter(chapter);
    try {
      const result = await startQueue.mutateAsync({
        chapter,
        ...(topic ? { topic } : {}),
        limit: 30,
        kind,
      });
      const items = result.cards;
      if (items.length === 0) {
        const release = result.meta.release;
        toast.error(
          release === undefined
            ? t("flashcards.review.empty.title")
            : release.opensAt !== null
              ? t("flashcards.topic.opens", {
                  date: formatDayShort(dayKey(release.opensAt, DEFAULT_TIMEZONE), lang),
                })
              : t("flashcards.topic.closed"),
        );
        return;
      }
      saveSession(createReviewSession(items, { chapter, ...(topic ? { topic } : {}) }));
      navigate("/flashcards/review");
    } catch (error) {
      toast.error(messageForError(error, t));
    } finally {
      setStartingChapter(null);
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title={t("flashcards.nav")} description={t("flashcards.description")} />

      {unfinished && (
        <Button className="mb-6" onClick={() => navigate("/flashcards/review")}>
          {t("flashcards.deck.resume")}
        </Button>
      )}

      {!decksQuery.isLoading && (
        <div className="mb-6">
          <MixedCard mixed={decksQuery.data?.mixed} onStart={requestMixed} />
        </div>
      )}

      <div className="mb-6">
        <StatsPanel />
      </div>

      {decksQuery.isLoading ? (
        <Spinner size="md" label={t("common.loading")} />
      ) : (
        <details>
          <summary className="mb-4 cursor-pointer text-sm font-medium text-ink-700 dark:text-ink-200">
            {t("flashcards.decks.browse")}
          </summary>
          <div className="space-y-4">
            {(decksQuery.data?.decks ?? []).map((deck) => {
              const topicOptions = [
                { value: "", label: t("flashcards.deck.topicFilter.all") },
                ...(topicsQuery.data?.topics ?? [])
                  .filter((topic) => topic.chapter === deck.chapter)
                  .map((topic) => ({ value: topic.id, label: localize(topic.name) })),
              ];
              return (
                <DeckCard
                  key={deck.chapter}
                  deck={deck}
                  topic={topicByChapter[deck.chapter] ?? ""}
                  topicOptions={topicOptions}
                  onTopicChange={(topic) =>
                    setTopicByChapter((prev) => ({ ...prev, [deck.chapter]: topic }))
                  }
                  onStart={(kind) => requestStart(deck.chapter, kind)}
                  starting={startingChapter === deck.chapter}
                />
              );
            })}
          </div>
        </details>
      )}
      <Modal
        open={replacement !== null}
        onClose={() => setReplacement(null)}
        title={t("flashcards.deck.replace.title")}
        description={t("flashcards.deck.replace.body")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setReplacement(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (!replacement) return;
                const chosen = replacement;
                setReplacement(null);
                if (chosen.mixed) navigate("/flashcards/review?start=mixed");
                else void start(chosen.chapter, chosen.kind);
              }}
            >
              {t("flashcards.deck.replace.confirm")}
            </Button>
          </>
        }
      />
    </div>
  );
}

export default FlashcardsPage;
